import { useEffect, useMemo, useRef, useState } from 'react';
/* Entry package (export default class) — bản dist/*.js là IIFE không export, webpack không tạo constructor đúng */
import Gantt from 'frappe-gantt';

function toGanttTasks(tasks) {
  return (tasks || []).map((t) => ({
    id: String(t.id),
    name: String(t.name || ''),
    start: t.start,
    end: t.end,
    progress: typeof t.progress === 'number' ? t.progress : Number(t.progress) || 0,
    dependencies: t.dependencies || '',
  }));
}

/**
 * Pan: Shift + trái hoặc nút giữa trên vùng .gantt-container.
 * mousedown ở capture phase trên <strong>window</strong> chạy trước mọi listener trên SVG → stopPropagation chặn hẳn frappe-gantt (kể cả khi pointer events không đồng bộ).
 */
function attachGanttPan(mountRoot) {
  let drag = false;
  let scrollEl = /** @type {HTMLElement | null} */ (null);
  let startX = 0;
  let startY = 0;
  let startSl = 0;
  let startSt = 0;

  function findContainer(e) {
    const t = e.target;
    if (!(t instanceof Element)) return null;
    const c = t.closest('.gantt-container');
    if (!c || !mountRoot.contains(c)) return null;
    return /** @type {HTMLElement} */ (c);
  }

  function wantsPan(e) {
    const middle = e.button === 1;
    const shiftLeft = e.button === 0 && e.shiftKey;
    return middle || shiftLeft;
  }

  function onWindowMouseMove(e) {
    if (!drag || !scrollEl) return;
    e.preventDefault();
    scrollEl.scrollLeft = startSl - (e.clientX - startX);
    scrollEl.scrollTop = startSt - (e.clientY - startY);
  }

  function onWindowMouseUp(e) {
    if (!drag) return;
    drag = false;
    if (scrollEl) scrollEl.style.cursor = '';
    scrollEl = null;
    window.removeEventListener('mousemove', onWindowMouseMove, true);
    window.removeEventListener('mouseup', onWindowMouseUp, true);
  }

  /** capture tại window: trước cả .gantt-container và SVG */
  function onWindowMouseDownCapture(e) {
    const c = findContainer(e);
    if (!c || !wantsPan(e)) return;
    e.preventDefault();
    e.stopPropagation();
    drag = true;
    scrollEl = c;
    startX = e.clientX;
    startY = e.clientY;
    startSl = c.scrollLeft;
    startSt = c.scrollTop;
    c.style.cursor = 'grabbing';
    window.addEventListener('mousemove', onWindowMouseMove, true);
    window.addEventListener('mouseup', onWindowMouseUp, true);
  }

  function onMountAuxClick(e) {
    if (e.button === 1) e.preventDefault();
  }

  function onMountWheel(e) {
    if (!e.shiftKey) return;
    const c = findContainer(e);
    if (!c) return;
    e.preventDefault();
    c.scrollLeft += e.deltaY;
  }

  window.addEventListener('mousedown', onWindowMouseDownCapture, true);
  mountRoot.addEventListener('auxclick', onMountAuxClick);
  mountRoot.addEventListener('wheel', onMountWheel, { passive: false });

  return () => {
    window.removeEventListener('mousedown', onWindowMouseDownCapture, true);
    window.removeEventListener('mousemove', onWindowMouseMove, true);
    window.removeEventListener('mouseup', onWindowMouseUp, true);
    if (scrollEl) scrollEl.style.cursor = '';
    drag = false;
    scrollEl = null;
    mountRoot.removeEventListener('auxclick', onMountAuxClick);
    mountRoot.removeEventListener('wheel', onMountWheel);
  };
}

/**
 * frappe-gantt 0.6: mảng task rỗng sẽ làm crash (reduce trên []).
 */
export default function ConstructionGantt({ tasks, viewMode = 'Day', suppressEmptyHint = false }) {
  const wrapRef = useRef(null);
  const [error, setError] = useState(null);
  const data = useMemo(() => toGanttTasks(tasks), [tasks]);
  const isEmpty = data.length === 0;

  useEffect(() => {
    setError(null);
    const el = wrapRef.current;
    if (!el) return undefined;

    el.innerHTML = '';
    if (isEmpty) {
      return undefined;
    }

    let detachPan = null;
    try {
      new Gantt(el, data, { view_mode: viewMode });
      if (el.querySelector('.gantt-container')) detachPan = attachGanttPan(el);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : String(e));
      el.innerHTML = '';
    }

    return () => {
      if (detachPan) detachPan();
      el.innerHTML = '';
    };
  }, [data, viewMode, isEmpty]);

  return (
    <div className="construction-gantt-viewport">
      {!isEmpty && !error ? (
        <p className="gantt-hint">
          <strong>Di chuyển vùng xem:</strong> giữ <kbd>Shift</kbd> và kéo chuột trái (kể cả trên thanh công việc), hoặc{' '}
          <strong>nút giữa chuột</strong> và kéo. Giữ <kbd>Shift</kbd> và lăn bánh xe để trượt ngang. Cuộn chuột (không
          Shift) trong khung để lên/xuống.
        </p>
      ) : null}
      {error && (
        <div className="gantt-error-box" role="alert">
          <strong>Lỗi vẽ Gantt:</strong> {error}
        </div>
      )}
      {isEmpty && !error && !suppressEmptyHint && <p className="gantt-empty-hint">Không có công việc để hiển thị trên biểu đồ.</p>}
      <div
        ref={wrapRef}
        className="gantt-mount-root"
        style={{
          minHeight: 400,
          width: '100%',
          minWidth: 0,
          display: isEmpty || error ? 'none' : 'block',
        }}
        aria-hidden={isEmpty || error}
      />
    </div>
  );
}
